import type { Row, DashboardData } from "./types";

const BASE = "/api";

/**
 * Usuário logado nesta sessão. É enviado no header das operações de usuários
 * para o servidor validar que apenas administradores criam/editam/excluem.
 */
let sessionUser = "";
export function setSessionUser(u: string) {
  sessionUser = u ?? "";
}
const authHeaders = () => ({ "x-sercamp-usuario": sessionUser });

export class ApiError extends Error {
  code: string;
  /** Status HTTP da resposta (0 quando o erro não veio de uma resposta). */
  status: number;
  constructor(message: string, code = "ERROR", status = 0) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

async function handle(res: Response) {
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    let code = "ERROR";
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
      if (body?.code) code = body.code;
    } catch {
      /* ignora */
    }
    throw new ApiError(msg, code, res.status);
  }
  return res.json();
}

/** Dados do despacho de uma análise (resultado + parecer + laudo). */
export interface DespacharDados {
  resultado: string; // Aprovado | Reprovado | Com ressalvas
  parecer?: string;
  laudoNome?: string;
  laudoMime?: string;
  laudoBase64?: string;
  responsavel?: string;
}

/** Espelha DadosLaudo de server/gerar-laudo.ts — os campos das 4 seções do laudo. */
export interface DadosLaudo {
  identificacao?: Record<string, string>;
  fisicoQuimica?: Record<string, string>;
  agdGases?: Record<string, string>;
  pcb?: Record<string, string>;
  furfuraldeido?: Record<string, string>;
}

/**
 * Uma amostra que ainda não pode ser liberada por causa da APROVAÇÃO do laudo.
 *
 * `estado` diz de quem é a bola: "sem-pedido" e "reprovado" são de quem
 * analisou (dá para reenviar), "pendente" é do aprovador e "vencido" quer dizer
 * que a análise mudou depois de aprovada — a aprovação caiu junto.
 */
export interface PendenciaAprovacao {
  id: number;
  amostra: string;
  motivo: string;
  /** Dá para mandar (ou remandar) para a fila de aprovação agora? */
  pode_pedir: boolean;
  estado: "sem-pedido" | "pendente" | "reprovado" | "vencido";
}

/**
 * Uma pendência que RETÉM a OS — o motivo pelo qual um trabalho pronto não
 * pode ser entregue (pagamento, documento, decisão do comercial).
 *
 * Enquanto houver uma com `situacao === "Aberta"`, o servidor recusa a
 * liberação e o despacho daquela OS.
 */
export interface PendenciaOs {
  id: number;
  os_numero: string;
  /** Pagamento | Documentação | Comercial | Outro */
  tipo: string;
  descricao: string | null;
  /** Aberta | Resolvida */
  situacao: string;
  registrada_por: string | null;
  registrada_em: string;
  resolvida_por: string | null;
  resolvida_em: string | null;
  resolucao: string | null;
}

/* ==================== Diagnóstico de DGA (NBR 7274) ==================== */

/** Uma das três razões da norma, com a divisão à vista. */
export interface RazaoDGA {
  chave: "r1" | "r2" | "r3";
  formula: string;
  numerador: { gas: string; valor: number | null };
  denominador: { gas: string; valor: number | null };
  valor: number | null;
  /** Por que não deu para calcular (denominador zero, gás não informado). */
  motivo?: string;
}

/** Uma classe de falha cadastrada (DP, D1, D2, T1, T2, T3) ou a linha NORMAL. */
export interface ClasseDGA {
  id: number;
  caso: string;
  rotulo: string;
  norma: string | null;
  revisao: string | null;
  /** A frase padronizada que sai no laudo (cadastrada). */
  texto_laudo: string | null;
  /** Em quantos meses recoletar por causa desta classe (cadastrado). */
  reamostragem_meses: number | null;
  observacao: string | null;
}

/** Uma comparação entre duas análises consecutivas. */
export interface PassoDGA {
  de: { analiseId: number; data: string | null; valor: number | null };
  para: { analiseId: number; data: string | null; valor: number | null };
  meses: number | null;
  variacaoAbsoluta: number | null;
  variacaoPercentual: number | null;
  taxaMensalPercentual: number | null;
  observacao?: string;
}

/** A evolução de UM gás ao longo das análises. */
export interface EvolucaoGasDGA {
  chave: string;
  rotulo: string;
  anterior: { valor: number | null; data: string | null; analiseId: number | null };
  atual: { valor: number | null; data: string | null; analiseId: number | null };
  meses: number | null;
  variacaoAbsoluta: number | null;
  variacaoPercentual: number | null;
  taxaMensalPercentual: number | null;
  acumulado: {
    variacaoAbsoluta: number | null;
    variacaoPercentual: number | null;
    taxaMensalPercentual: number | null;
    meses: number | null;
    primeiraData: string | null;
    ultimaData: string | null;
  } | null;
  tendencia: "crescente" | "decrescente" | "estável" | "sem dados suficientes";
  passos: PassoDGA[];
  observacao?: string;
}

export interface EvolucaoDGA {
  temHistorico: boolean;
  /** Como as análises anteriores foram encontradas — muda a confiança no dado. */
  correlacao: "equipamento" | "cliente-equipamento" | "nenhuma";
  analises: number;
  pontos: { analiseId: number; data: string | null; os: string | null; valores: Record<string, number | null> }[];
  gases: EvolucaoGasDGA[];
  alertaAcetileno: { ativo: boolean; texto: string; motivo: string; tipo?: string } | null;
  mensagem: string;
  convencao: string;
}

/** O resultado do cálculo das razões contra a tabela cadastrada. */
export interface RazoesDGA {
  calculado: boolean;
  razoes: RazaoDGA[];
  /** A única condição em que as três razões cabem ao mesmo tempo (ou null). */
  caso: ClasseDGA | null;
  /** Todas as condições atendidas; mais de uma = o sistema não escolhe. */
  candidatos?: ClasseDGA[];
  /** Condição por condição: true dentro, false fora, null = NS (não restringe). */
  avaliacao?: {
    caso: string;
    rotulo: string;
    r1: boolean | null;
    r2: boolean | null;
    r3: boolean | null;
    enquadra: boolean;
  }[];
  regrasCadastradas: number;
  mensagem: string;
}

/** Um gás comparado com o valor típico da NBR 7274 (Tabela A.2). */
export interface GasTipicoDGA {
  chave: string;
  rotulo: string;
  valorInformado: string | null;
  valor: number | null;
  /** "≤ 50 ppm", quando houve um valor típico aplicável. */
  tipico: string | null;
  limiteId: number | null;
  situacao: "abaixo" | "acima" | "sem-valor" | "nao-numerico" | "ambiguo" | "falta-dado" | "fora-do-escopo";
  explicacao: string;
}

/** A primeira pergunta da norma: os gases estão dentro do típico? (9.1) */
export interface ValoresTipicosDGA {
  /** Os valores típicos estão ligados para os cinco gases das relações? */
  emUso: boolean;
  desligados: string[];
  gases: GasTipicoDGA[];
  /** Todos dentro do típico = condição normal automática. */
  todosAbaixo: boolean;
  faltando: { eixo: string; rotulo: string; comoResolver: string; gases: string[] }[];
  mensagem: string;
}

/** O estado da tela de DGA (GET, não grava nada). */
export interface EstadoDGA {
  analise: { id: number; os: string | null; cliente: string | null; equipamento: string | null; despachada: boolean };
  concentracoes: Record<string, number | null>;
  razoes: RazoesDGA;
  /** A comparação com os valores típicos (pode vir ausente de servidor antigo). */
  tipicos?: ValoresTipicosDGA;
  /** O que o botão "Gerar" faria agora — mostrado antes do clique. */
  automatico?: {
    motivo: "tipicos" | "razoes" | null;
    ehNormal: boolean;
    classe: ClasseDGA | null;
    texto: string;
    bloqueio: string | null;
    mensagem: string;
  };
  classes: ClasseDGA[];
  normal: ClasseDGA | null;
  evolucao: EvolucaoDGA;
  /** O diagnóstico já gravado, com a origem e o original da IA. */
  salvo: Record<string, any>;
  parecerFechado: boolean;
  avisoCadastro: string | null;
}

/** A resposta dos três caminhos que gravam. */
export interface RespostaDGA {
  ok: boolean;
  origem: "normal" | "ia" | "manual";
  classe?: ClasseDGA | null;
  classeCalculada?: ClasseDGA | null;
  substituiu?: boolean;
  concentracoes?: Record<string, number | null>;
  razoes?: RazoesDGA;
  evolucao?: EvolucaoDGA;
  /** O que ficou gravado no parecer (texto e prazo podem voltar vazios). */
  texto: string;
  reamostragem: string;
  status?: string;
  /** Algo foi escrito: condição normal (valores típicos) ou condição da tabela. */
  enquadrado?: boolean;
  /** De onde saiu o diagnóstico gerado. */
  motivo?: "tipicos" | "razoes" | null;
  tipicos?: ValoresTipicosDGA;
  /** Gases no típico, mas o Normal automático foi segurado (acetileno aumentando). */
  bloqueio?: string | null;
  alertaAcetileno?: { ativo: boolean; texto: string; motivo: string; tipo?: string } | null;
  aviso?: string | null;
  mensagem: string;
}

/** Os quatro campos do diagnóstico de uma amostra. */
export interface DiagnosticoAmostra {
  /** O problema identificado a partir dos resultados. */
  diagnostico: string;
  /** O que fazer diante do diagnóstico. */
  recomendacao: string;
  /** Se (e quando) coletar nova amostra. */
  reamostragem: string;
  /** Normal | Atenção | Crítico. */
  status: string;
}

/** Quanto serviço em aberto cada ensaio tem (contadores do menu lateral). */
export interface ResumoEnsaios {
  /** Chave do ensaio → OS em aberto, amostras e quantas OS passaram do prazo. */
  ensaios: Record<string, { os: number; amostras: number; atrasadas: number }>;
  total: { os: number; amostras: number };
  /** OS (e amostras) esperando a entrada no laboratório — o número do Protocolo. */
  protocolo?: { pendentes: number; amostras: number };
}

/* ======================  PROTOCOLO (CONTROLE DE OS)  ====================== */

/** As sete situações do fluxo, na ordem (ver server/protocolo.ts). */
export type EtapaProtocolo =
  | "pendente"
  | "recebida"
  | "em_analise"
  | "analise_concluida"
  | "em_diagnostico"
  | "diagnostico_concluido"
  | "relatorio_gerado";

/** Uma OS na lista do Protocolo — a situação é a da amostra mais atrasada. */
export interface OsProtocolo {
  os_numero: string;
  cliente: string | null;
  etapa: EtapaProtocolo;
  etapa_rotulo: string;
  por_etapa: Partial<Record<EtapaProtocolo, number>>;
  amostras: number;
  pendentes: number;
  /** Amostras pendentes ainda sem equipamento do cadastro (não podem entrar). */
  sem_equipamento: number;
  ensaios: string[];
  origens: string[];
  solicitado_em: string | null;
  prazo_retorno: string | null;
  entrada: string | null;
  quantidade_prevista: number | null;
  quantidade_recebida: number | null;
  divergente: boolean;
  frascos: { previsto: number; bipados: number } | null;
}

/** Uma amostra (um equipamento) dentro da OS do Protocolo. */
export interface AmostraProtocolo {
  id: number;
  os_numero: string;
  nome: string;
  cliente: string | null;
  equipamento: string | null;
  equipamento_id: number | null;
  equip: {
    tag: string | null;
    numero_serie: string | null;
    fabricante: string | null;
    tipo_equipamento: string | null;
    proprietario: string | null;
  } | null;
  serie_informada: string | null;
  numero_amostra: string | null;
  data_coleta: string | null;
  data_coleta_texto: string | null;
  temperatura_coleta: string | null;
  condicao_oleo: string | null;
  ensaios: string[];
  ensaios_definidos: boolean;
  concluidas: string[];
  preenchidas: string[];
  com_parecer: string[];
  status: string | null;
  origem: string | null;
  solicitante: string | null;
  solicitado_em: string | null;
  prazo_coleta: string | null;
  prazo_retorno: string | null;
  entrada_lab_em: string | null;
  entrada_lab_por: string | null;
  recebimento_id: number | null;
  diag_finalizado_em: string | null;
  laudo_gerado_em: string | null;
  laudo_nome: string | null;
  despachado_em: string | null;
  etapa: EtapaProtocolo;
  etapa_rotulo: string;
}

/** Uma conferência de chegada (o que o campo disse × o que chegou). */
export interface RecebimentoProtocolo {
  id: number;
  data_entrada: string;
  quantidade_prevista: number;
  quantidade_recebida: number;
  divergencia: string | null;
  observacao: string | null;
  recebido_por: string | null;
  registrado_em: string;
  amostras: number;
}

export interface DetalheOsProtocolo {
  os_numero: string;
  cliente: string | null;
  etapa: EtapaProtocolo;
  etapa_rotulo: string;
  amostras: AmostraProtocolo[];
  recebimentos: RecebimentoProtocolo[];
  material: { previsto: number; bipados: number; status: string | null } | null;
  etapas: { key: EtapaProtocolo; rotulo: string }[];
}

/**
 * O retrato da OS para EDITAR e EXCLUIR (ver server/os-gerenciar.ts): os dados
 * gerais e tudo o que uma exclusão levaria junto.
 */
export interface ResumoGerenciarOs {
  os_numero: string;
  cliente: string | null;
  solicitante: string | null;
  prazo_retorno: string | null;
  prazo_coleta: string | null;
  /** Campos em que as amostras da OS discordam (salvar iguala todas). */
  divergentes: ("cliente" | "solicitante" | "prazo_retorno" | "prazo_coleta")[];
  /** Por que o número não pode mudar (null = pode). */
  numero_travado: string | null;
  origens: string[];
  amostras: number;
  amostras_lista: { id: number; nome: string; entregue: boolean; com_trabalho: boolean }[];
  entregues: number;
  com_trabalho: number;
  recebimentos: number;
  pendencias: { abertas: number; total: number };
  liberada: { em: string; por: string | null } | null;
  remessa: { remessas: number; bipados: number } | null;
}

/** O equipamento achado pelo nº de série (cadastro da aba Equipamentos). */
export interface EquipamentoBusca {
  id: number;
  tag: string | null;
  tipo_equipamento: string | null;
  cliente: string | null;
  fabricante: string | null;
  numero_serie: string | null;
  potencia_kva: string | null;
  tensao_superior_kv: string | null;
  local_instalacao: string | null;
  ativo: boolean;
  analises: number;
}

/** A conferência de chegada que acompanha a entrada. */
export interface DadosEntradaProtocolo {
  data_entrada: string;
  quantidade_prevista?: number;
  quantidade_recebida?: number;
  divergencia?: string;
  observacao?: string;
}

/** Um equipamento na criação manual da OS. */
export interface AmostraNovaProtocolo {
  equipamento_id: number;
  analises: string[];
  numero_amostra?: string;
  data_coleta?: string;
  temperatura_coleta?: string;
  condicao_oleo?: string;
}

/** Um campo dos relatórios (modelos GD/PCB): o que vai, e o que o sistema poria sozinho. */
export interface CampoRelatorio {
  valor: string;
  automatico: string;
  /** De onde vem o automático ("cadastro do equipamento", "Protocolo"…). */
  origem: string | null;
}

/** A prévia dos relatórios da amostra (server/relatorio-dados.ts), sem gerar nada. */
export interface RelatorioDados {
  campos: Record<string, CampoRelatorio>;
  automaticos: {
    cliente: string;
    dataEntrada: string;
    dataAnalise: { fisicoQuimica: string; agdGases: string; pcb: string };
  };
  ensaios: {
    ensaio: string;
    modelo: "novo" | "antigo";
    /** Nº já dado a este relatório (null = ainda não). */
    numero: string | null;
    /** O que ele vai receber quando o parecer for finalizado e o relatório gerado. */
    proximo: string | null;
    parecerFinalizado: boolean;
  }[];
}

/** Uma análise no histórico do equipamento (a atual ou uma anterior). */
export interface ItemHistoricoEquipamento {
  id: number;
  os_numero: string;
  numero_amostra?: string | null;
  motivo?: string | null;
  /** A data que representa a análise: coleta → entrada → solicitação. */
  data: string | null;
  data_coleta_texto: string | null;
  entrada_lab_em: string | null;
  despachado_em: string | null;
  status: string | null;
  temperatura_coleta: string | null;
  condicao_oleo: string | null;
  ensaios: string[];
  /** Por ensaio, só os campos com valor. */
  valores: Record<string, Record<string, string>>;
  pareceres: Record<string, { status?: string | null; descricao?: string | null; finalizado?: boolean }>;
  diag_status: string | null;
}

export interface HistoricoEquipamento {
  equipamento: Record<string, any> | null;
  /** Como as análises foram reconhecidas: pelo cadastro ou pelo nº de série. */
  criterio: "cadastro" | "serie" | null;
  atual: ItemHistoricoEquipamento;
  anteriores: ItemHistoricoEquipamento[];
}

/** Registro do HISTORICO — uma análise de óleo já DESPACHADA pelo Laboratório. */
export interface HistoricoItem {
  id: number;
  os_numero: string;
  cliente: string | null;
  equipamento: string | null; // tipo de equipamento
  resultado: string | null;
  data_recebimento: string | null;
  despachado_em: string | null;
  laudo_nome: string | null;
  tem_laudo: boolean;
}

/** A análise inteira, como ela foi despachada (tela de visualização do laudo). */
export interface LaudoCompleto {
  id: number;
  os_numero: string;
  cliente: string | null;
  equipamento: string | null;
  solicitante: string | null;
  responsavel: string | null;
  status: string | null;
  resultado: string | null;
  parecer: string | null;
  prazo_coleta: string | null;
  prazo_retorno: string | null;
  data_recebimento: string | null;
  despachado_em: string | null;
  created_at: string | null;
  /** As quatro seções do laudo, como foram preenchidas (JSONB). */
  identificacao: Record<string, string> | null;
  fisico_quimica: Record<string, string> | null;
  agd_gases: Record<string, string> | null;
  pcb: Record<string, string> | null;
  furfuraldeido: Record<string, string> | null;
  /** Seções que o Relatórios pediu; vazio/nulo = todas. */
  analises_selecionadas: string[] | null;
  laudo_nome: string | null;
  laudo_mime: string | null;
  tem_laudo: boolean;
  laudo_bytes: number | null;
}

/** A conferência do laudo (aba "Aprovação de relatório"), quando houve. */
export interface AprovacaoLaudo {
  status: string;
  solicitado_por: string | null;
  solicitado_em: string | null;
  avaliado_por: string | null;
  avaliado_em: string | null;
  motivo: string | null;
}

/** Um item já conferido (bipado) de uma remessa. */
export interface ItemMaterial {
  id: number;
  codigo: string;
  descricao: string | null;
  tipo: string | null;
  bipado_por: string | null;
  bipado_em: string;
  /** Como o item foi conferido: "leitor" (mão/digitado) ou "camera". */
  origem?: string | null;
}

/** Estado da conferência de uma remessa. */
export interface ConferenciaMaterial {
  remessa: Row;
  itens: ItemMaterial[];
  conferido: number;
  previsto: number;
  diferenca: number;
}

/**
 * Resposta de uma bipagem. `duplicado` NÃO é erro: o leitor lê o mesmo frasco
 * duas vezes com frequência, e a tela precisa mostrar isso em amarelo e seguir
 * o trabalho — por isso vem como resposta, e não como exceção.
 */
export interface ResultadoBipagem {
  ok: boolean;
  duplicado?: boolean;
  mensagem?: string;
  item?: ItemMaterial;
  conferido: number;
  previsto: number;
  diferenca?: number;
  completo?: boolean;
}

export const api = {
  /**
   * PLANILHA da Análise de EMMS — exportar, baixar o modelo e importar.
   *
   * Genérico no `recurso` de propósito: hoje só a EMMS usa, mas o servidor
   * expõe o mesmo trio de rotas para qualquer aba que venha a precisar
   * (`/api/<recurso>/planilha/...`). Ver server/emms.ts.
   */
  planilha: {
    /** Baixa o .xlsx (a lista atual, ou o modelo em branco). */
    baixar: async (recurso: string, qual: "exportar" | "modelo"): Promise<void> => {
      const res = await fetch(`${BASE}/${recurso}/planilha/${qual}`, { headers: authHeaders() });
      if (!res.ok) {
        let msg = `Erro ${res.status}`;
        try {
          msg = (await res.json())?.error ?? msg;
        } catch {
          /* corpo não-JSON */
        }
        throw new ApiError(msg);
      }
      // O nome vem do servidor (Content-Disposition); sem ele, um nome sensato.
      const cd = res.headers.get("content-disposition") ?? "";
      const doServidor = /filename\*=UTF-8''([^;]+)/.exec(cd)?.[1];
      const nome = doServidor ? decodeURIComponent(doServidor) : `${recurso}-${qual}.xlsx`;
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    },

    /**
     * Manda a planilha. Sem `confirmar`, o servidor NÃO grava: devolve a prévia
     * do que aconteceria (quantos entram, quantos são atualizados, o que foi
     * ignorado) — é ela que a tela mostra antes de perguntar "pode gravar?".
     */
    importar: (
      recurso: string,
      arquivo: File,
      confirmar = false,
    ): Promise<{
      previa?: boolean;
      ok?: boolean;
      criar?: number;
      atualizar?: number;
      criados?: number;
      atualizados?: number;
      ignoradas?: string[];
      repetidas?: string[];
      colunasReconhecidas?: string[];
      colunasIgnoradas?: string[];
      amostra?: { codigo: string; acao: string; descricao: string | null }[];
      mensagem?: string;
    }> => {
      const fd = new FormData();
      fd.append("file", arquivo);
      return fetch(`${BASE}/${recurso}/planilha/importar${confirmar ? "?confirmar=1" : ""}`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },
  },

  list: (resource: string): Promise<Row[]> =>
    fetch(`${BASE}/${resource}`, { headers: authHeaders() }).then(handle),

  create: (resource: string, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  update: (resource: string, id: number, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  remove: (resource: string, id: number): Promise<{ ok: boolean }> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    }).then(handle),

  dashboard: (): Promise<DashboardData> =>
    fetch(`${BASE}/dashboard`, { headers: authHeaders() }).then(handle),

  /** Retorna { usuario, nome, cargo, isAdmin, podeExcluir } do usuário informado.
   * `podeExcluir` = só administradores (regra do Portal); a tela usa para esconder
   * os botões de excluir de quem não é admin. */
  me: (
    usuario: string,
  ): Promise<{
    usuario: string;
    nome: string | null;
    cargo: string | null;
    isAdmin: boolean;
    podeExcluir: boolean;
  }> => fetch(`${BASE}/me?u=${encodeURIComponent(usuario)}`).then(handle),

  /** Análises de óleo — configuração do laudo, geração do docx e despacho ao Relatórios. */
  analises: {
    /**
     * Os números de cada ensaio (FQ/CR/PCB/2-FALL) — é o que acende os
     * contadores do submenu de "Análises" na barra lateral.
     */
    resumoEnsaios: (): Promise<ResumoEnsaios> =>
      fetch(`${BASE}/analises/resumo-ensaios`, { headers: authHeaders() }).then(handle),

    /**
     * "Análise concluída" — a confirmação MANUAL de um ensaio da amostra.
     * Nunca é deduzida do preenchimento: quem diz que acabou é a bancada.
     */
    concluir: (id: number, secao: string): Promise<{ ok: boolean; concluido_em: string; concluido_por: string | null }> =>
      fetch(`${BASE}/analises_oleo/${id}/concluir`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ secao }),
      }).then(handle),

    /**
     * Os QUATRO CAMPOS do diagnóstico da amostra (diagnóstico, recomendação,
     * reamostragem e status). `origem` registra se o texto veio da IA.
     */
    salvarDiagnosticoAmostra: (
      id: number,
      secao: string,
      dados: DiagnosticoAmostra & { origem?: "ia" | "manual" | "regras" },
    ): Promise<{ ok: boolean; diagnostico: Row; secao: string }> =>
      fetch(`${BASE}/analises_oleo/${id}/diagnostico-amostra`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ ...dados, secao }),
      }).then(handle),

    /**
     * Monta diagnóstico, status e recomendação pelas REGRAS do laboratório —
     * sem IA, sem custo e com o mesmo texto sempre para a mesma amostra.
     * Também não grava: a pessoa lê, corrige e salva.
     *
     * `detalhe` traz ensaio por ensaio a zona (dentro/próximo/fora) e o motivo,
     * que é o que se confere antes de assinar.
     */
    gerarDiagnosticoAutomatico: (
      id: number,
      secao: string,
    ): Promise<{
      ok: boolean;
      sugestao: { diagnostico: string; recomendacao: string; status: string };
      /** Quantos dos ensaios avaliados têm margem de proximidade definida. */
      comMargem: number;
      avisos: string[];
      detalhe: {
        rotulo: string;
        valor: string;
        unidade: string | null;
        zona: "dentro" | "proximo" | "fora";
        motivo: string;
        norma: string | null;
      }[];
    }> =>
      fetch(`${BASE}/analises_oleo/${id}/diagnostico-automatico`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ secao }),
      }).then(handle),

    /**
     * Pede à IA uma SUGESTÃO dos quatro campos, a partir dos resultados da
     * amostra e dos critérios cadastrados. NÃO grava nada: quem grava é a
     * pessoa, depois de ler e corrigir.
     */
    sugerirDiagnosticoIA: (
      id: number,
      secao: string,
    ): Promise<{
      ok: boolean;
      /**
       * `false` = a IA NÃO foi chamada porque nenhum ensaio tinha limite
       * cadastrado para comparar. `sugestao` vem vazia de propósito e a tela
       * NÃO deve sobrescrever o que a pessoa já escreveu — só mostrar o
       * `aviso`, que explica o que falta. Ver server/diagnostico-amostra.ts.
       */
      gerado: boolean;
      sugestao: DiagnosticoAmostra;
      aviso: string | null;
      /**
       * Diferente de `aviso`: isto PEDE AÇÃO antes de salvar — o texto citou
       * ensaio sem limite, ou o status da IA divergiu do que os limites dão.
       * A tela mostra em caixa vermelha, separada da dica.
       */
      alerta: string | null;
      criterios: number;
      limites: number;
      /** Quantos ensaios o parecer realmente cobre. */
      avaliados: number;
      /** Ensaios medidos que ficaram de fora por falta de limite. */
      sem_referencia: string[];
    }> =>
      fetch(`${BASE}/analises_oleo/${id}/diagnostico-ia`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ secao }),
      }).then(handle),

    /* ---------------- Diagnóstico de DGA (NBR 7274) ---------------- */

    /**
     * O ESTADO da tela de DGA, sem gravar nada: concentrações, as três razões
     * já calculadas, as classes cadastradas para escolha manual, a evolução
     * mensal dos gases e o alerta de acetileno.
     *
     * Buscado ao ABRIR a aba — o analista precisa ver a conta antes de decidir
     * qual dos três caminhos seguir.
     */
    dga: (id: number): Promise<EstadoDGA> =>
      fetch(`${BASE}/analises_oleo/${id}/dga`, { headers: authHeaders() }).then(handle),

    /** §3: registra condição normal de operação (texto e prazo do cadastro). */
    dgaNormal: (id: number): Promise<RespostaDGA> =>
      fetch(`${BASE}/analises_oleo/${id}/dga/normal`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: "{}",
      }).then(handle),

    /**
     * §4: calcula as razões, enquadra na tabela cadastrada e grava a frase da
     * condição + o prazo de reamostragem. Nada passa por modelo de linguagem.
     */
    dgaGerar: (id: number): Promise<RespostaDGA> =>
      fetch(`${BASE}/analises_oleo/${id}/dga/ia`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: "{}",
      }).then(handle),

    /** §7: o analista escolhe a classe; o resultado calculado fica gravado. */
    dgaManual: (id: number, caso: string, justificativa: string): Promise<RespostaDGA> =>
      fetch(`${BASE}/analises_oleo/${id}/dga/manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ caso, justificativa }),
      }).then(handle),

    /**
     * FINALIZA o diagnóstico da amostra (só quem tem a permissão).
     * NÃO libera nada para o cliente — a liberação é por OS, à parte.
     */
    finalizarDiagnostico: (
      id: number,
      secao: string,
    ): Promise<{
      ok: boolean;
      secao: string;
      finalizado_em: string | null;
      finalizado_por: string | null;
      /** A amostra INTEIRA fechou? É isso que libera o envio ao cliente. */
      amostraFinalizada: boolean;
      faltam: { secao: string; motivo: string }[];
      aviso: string | null;
    }> =>
      fetch(`${BASE}/analises_oleo/${id}/finalizar-diagnostico`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ secao }),
      }).then(handle),

    /** Reabre o diagnóstico finalizado (correção antes da liberação). */
    reabrirDiagnostico: (id: number, secao: string): Promise<{ ok: boolean }> =>
      // DELETE manda a seção na QUERY (corpo em DELETE é o erro clássico aqui).
      fetch(`${BASE}/analises_oleo/${id}/finalizar-diagnostico?secao=${encodeURIComponent(secao)}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),

    /** Reabre o ensaio (conclusão por engano, ou o ensaio vai ser refeito). */
    reabrir: (id: number, secao: string): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/analises_oleo/${id}/concluir?secao=${encodeURIComponent(secao)}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),

    /** Diagnóstico pelo motor de limites (ver server/diagnostico.ts). */
    diagnosticar: (id: number, comIA = false): Promise<any> =>
      fetch(`${BASE}/analises_oleo/${id}/diagnostico`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ comIA }),
      }).then(handle),

    lerDiagnostico: (id: number): Promise<any> =>
      fetch(`${BASE}/analises_oleo/${id}/diagnostico`, { headers: authHeaders() }).then(handle),

    solicitarAprovacao: (id: number): Promise<any> =>
      fetch(`${BASE}/analises_oleo/${id}/solicitar-aprovacao`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    situacaoAprovacao: (id: number): Promise<any> =>
      fetch(`${BASE}/analises_oleo/${id}/aprovacao`, { headers: authHeaders() }).then(handle),

    despachar: (id: number, dados: DespacharDados): Promise<{ ok: boolean; analise: Row }> =>
      fetch(`${BASE}/analises/${id}/despachar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    /** Salva (parcial, idempotente) os dados estruturados das 4 seções do laudo. */
    /**
     * Salva as seções do laudo — e, junto, a CONDIÇÃO DO ÓLEO da amostra, que é
     * coluna e não seção. É ela que escolhe a tabela da NBR 10576.
     */
    salvarConfig: (
      id: number,
      dados: DadosLaudo & { condicao_oleo?: string },
    ): Promise<{ ok: boolean; analise: Row }> =>
      fetch(`${BASE}/analises_oleo/${id}/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    /**
     * VINCULA (ou desvincula) o equipamento da amostra.
     *
     * O equipamento é de onde saem tipo de óleo, classe de tensão e tipo de
     * equipamento — os eixos que selecionam a faixa da norma. Sem vínculo, o
     * diagnóstico automático não tem como escolher limite nenhum.
     *
     * `equipamento_id: null` desfaz o vínculo.
     */
    vincularEquipamento: (
      id: number,
      equipamentoId: number | null,
    ): Promise<{ ok: boolean; analise: { id: number; equipamento_id: number | null } }> =>
      fetch(`${BASE}/analises_oleo/${id}/equipamento`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ equipamento_id: equipamentoId }),
      }).then(handle),

    /** Gera o laudo .docx a partir do template + dados já salvos. */
    gerarLaudo: (id: number): Promise<{ ok: boolean; analise: { id: number; laudo_nome: string; laudo_mime: string } }> =>
      fetch(`${BASE}/analises_oleo/${id}/gerar-laudo`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    /** Baixa o laudo .docx (preenchido) da análise e dispara o download no navegador. */
    baixarLaudo: async (id: number, fileName: string): Promise<void> => {
      const res = await fetch(`${BASE}/analises_oleo/${id}/laudo`, { headers: authHeaders() });
      if (!res.ok) {
        let msg = `Erro ${res.status}`;
        let code = "ERROR";
        try {
          const body = await res.json();
          if (body?.error) msg = body.error;
          if (body?.code) code = body.code;
        } catch {
          /* corpo não-JSON */
        }
        throw new ApiError(msg, code);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `analise-oleo-${id}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    },

    /**
     * GERAR O RELATÓRIO da amostra: monta o .docx de novo (com o diagnóstico
     * como está AGORA) e baixa. As duas chamadas, sempre: só baixar entregaria
     * o laudo gerado da última vez, que pode ser de antes do parecer fechado.
     */
    gerarRelatorio: async (id: number, osNumero: string): Promise<void> => {
      const r = await fetch(`${BASE}/analises_oleo/${id}/gerar-laudo`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle);
      const nome = r?.analise?.laudo_nome || `analise-oleo-OS-${osNumero}.docx`;
      await api.analises.baixarLaudo(id, nome);
    },

    /**
     * O RELATÓRIO DE UM ENSAIO SÓ — Gases e PCB nos modelos do laboratório, FQ
     * e Furanos no modelo antigo —, montado na hora com os dados de agora. O
     * nome do arquivo vem do servidor (leva o Nº do relatório).
     */
    baixarRelatorioEnsaio: async (id: number, ensaio: string): Promise<void> => {
      const res = await fetch(`${BASE}/analises_oleo/${id}/relatorio-ensaio/${encodeURIComponent(ensaio)}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        let msg = `Erro ${res.status}`;
        let code = "ERROR";
        try {
          const body = await res.json();
          if (body?.error) msg = body.error;
          if (body?.code) code = body.code;
        } catch {
          /* corpo não-JSON */
        }
        throw new ApiError(msg, code);
      }
      const nome =
        /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? `relatorio-${ensaio}-${id}.docx`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    },

    /** O que os relatórios vão mostrar (sugestões automáticas e Nº de cada um), sem gerar nada. */
    relatorioDados: (id: number): Promise<RelatorioDados> =>
      fetch(`${BASE}/analises_oleo/${id}/relatorio-dados`, { headers: authHeaders() }).then(handle),

    /** O histórico do equipamento da amostra (etapa de diagnóstico). */
    historicoEquipamento: (id: number): Promise<HistoricoEquipamento> =>
      fetch(`${BASE}/analises_oleo/${id}/historico-equipamento`, { headers: authHeaders() }).then(handle),
  },

  /**
   * PROTOCOLO (CONTROLE DE OS) — a entrada das amostras no laboratório.
   * Ver server/protocolo.ts.
   */
  protocolo: {
    listar: (): Promise<{ os: OsProtocolo[]; etapas: { key: EtapaProtocolo; rotulo: string }[] }> =>
      fetch(`${BASE}/protocolo`, { headers: authHeaders() }).then(handle),

    os: (numero: string): Promise<DetalheOsProtocolo> =>
      fetch(`${BASE}/protocolo/os/${encodeURIComponent(numero)}`, { headers: authHeaders() }).then(handle),

    /** Registra a entrada das amostras marcadas — é o gatilho das Análises. */
    receber: (
      numero: string,
      dados: DadosEntradaProtocolo & { amostras: number[] },
    ): Promise<{ ok: boolean; recebimento_id: number; liberadas: number; distribuicao: Record<string, number> }> =>
      fetch(`${BASE}/protocolo/os/${encodeURIComponent(numero)}/receber`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    desfazerEntrada: (id: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/protocolo/amostras/${id}/desfazer-entrada`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    /** Dados da coleta (nº, data, temperatura, condição; ensaios só pendente). */
    atualizarColeta: (
      id: number,
      dados: Partial<{
        numero_amostra: string;
        data_coleta: string;
        temperatura_coleta: string;
        condicao_oleo: string;
        analises: string[];
      }>,
    ): Promise<{ ok: boolean; amostra: AmostraProtocolo | null }> =>
      fetch(`${BASE}/protocolo/amostras/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    vincularEquipamento: (id: number, equipamentoId: number): Promise<{ ok: boolean; amostra: AmostraProtocolo | null }> =>
      fetch(`${BASE}/protocolo/amostras/${id}/equipamento`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ equipamento_id: equipamentoId }),
      }).then(handle),

    buscarEquipamentos: (q: string, cliente?: string, signal?: AbortSignal): Promise<{ itens: EquipamentoBusca[] }> =>
      fetch(
        `${BASE}/protocolo/equipamentos?q=${encodeURIComponent(q)}&cliente=${encodeURIComponent(cliente ?? "")}`,
        { headers: authHeaders(), signal },
      ).then(handle),

    proximoNumero: (): Promise<{ numero: string }> =>
      fetch(`${BASE}/protocolo/proximo-numero`, { headers: authHeaders() }).then(handle),

    criarOs: (dados: {
      os_numero: string;
      cliente: string;
      solicitante?: string;
      prazo_retorno?: string;
      amostras: AmostraNovaProtocolo[];
      entrada?: DadosEntradaProtocolo | null;
      /** true = acrescentar a uma OS que já existe (sem isto, número repetido é recusado). */
      acrescentar?: boolean;
    }): Promise<{ ok: boolean; os_numero: string; ids: number[]; recebimento_id: number | null; os_existente: boolean }> =>
      fetch(`${BASE}/protocolo/os`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    /** O que editar/excluir a OS precisam saber (dados gerais + o que cai junto). */
    gerenciar: (numero: string): Promise<ResumoGerenciarOs> =>
      fetch(`${BASE}/protocolo/os/${encodeURIComponent(numero)}/gerenciar`, { headers: authHeaders() }).then(
        handle,
      ),

    /** Corrige os dados gerais da OS em TODAS as amostras (só o que vier). */
    editarOs: (
      numero: string,
      dados: Partial<{
        os_numero: string;
        cliente: string;
        solicitante: string;
        prazo_retorno: string;
        prazo_coleta: string;
      }>,
    ): Promise<{ ok: boolean; os_numero: string; mudou: string[]; amostras: number }> =>
      fetch(`${BASE}/protocolo/os/${encodeURIComponent(numero)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    /** Apaga a OS inteira. `confirmar` = o número DIGITADO por quem exclui (o servidor confere). */
    excluirOs: (
      numero: string,
      opcoes: { remessa: boolean; confirmar: string },
    ): Promise<{ ok: boolean; os_numero: string; amostras: number; remessas: number }> =>
      fetch(
        `${BASE}/protocolo/os/${encodeURIComponent(numero)}?confirmar=${encodeURIComponent(opcoes.confirmar)}` +
          (opcoes.remessa ? "&remessa=1" : ""),
        { method: "DELETE", headers: authHeaders() },
      ).then(handle),

    /** Apaga uma amostra (e o que ela deixaria pendurado na OS). */
    excluirAmostra: (id: number): Promise<{ ok: boolean; os_numero: string; ultima_da_os: boolean }> =>
      fetch(`${BASE}/protocolo/amostras/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle),
  },

  /** O que a sessão pode fazer, e a gestão de quem pode o quê (admin). */
  permissoes: {
    /** As permissões do usuário logado (o App carrega uma vez e distribui). */
    minhas: (): Promise<{
      usuario: string | null;
      diagnostico: boolean;
      liberacao_cliente: boolean;
      adminSupremo?: boolean;
      /** Abas liberadas; VAZIO = sem restrição (vê todas). */
      abas?: string[];
    }> => fetch(`${BASE}/permissoes/me`, { headers: authHeaders() }).then(handle),

    listar: (): Promise<{
      permissoes: { usuario: string; permissao: string; concedido_por: string | null; concedido_em: string }[];
      usuarios: { usuario: string; nome: string | null; ativo: boolean }[];
      catalogo: { chave: string; rotulo: string; descricao: string }[];
      /** As abas do sistema, para marcar o que cada pessoa enxerga. */
      catalogoAbas: { key: string; rotulo: string; recurso: string | null }[];
      /**
       * O que o PORTAL diz sobre cada login (é lá que a conta existe e é lá que
       * se libera o sistema). `null` = não deu para perguntar — a tela então
       * não acusa ninguém.
       */
      situacoes: {
        usuario: string;
        existe: boolean | null;
        acesso: boolean | null;
        nome: string | null;
        /**
         * Entra por CADASTRO LOCAL do Laboratório — não depende da lista de
         * sistemas do Portal (é o caso do `admin`, anterior a esse controle).
         */
        local?: boolean;
      }[];
      /**
       * Quem já tem o LABORATÓRIO liberado no cadastro do Portal — a lista que
       * a tela oferece para conceder sem ninguém digitar login de cabeça.
       * `ok: false` = não deu para perguntar ao Portal; aí vale o campo livre.
       */
      candidatos: {
        ok: boolean;
        pessoas: { usuario: string; nome: string | null; cargo: string | null; email: string | null }[];
        mensagem: string | null;
      };
    }> => fetch(`${BASE}/permissoes`, { headers: authHeaders() }).then(handle),

    conceder: (usuario: string, permissao: string): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/permissoes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ usuario, permissao }),
      }).then(handle),

    revogar: (usuario: string, permissao: string): Promise<{ ok: boolean }> =>
      fetch(
        `${BASE}/permissoes?usuario=${encodeURIComponent(usuario)}&permissao=${encodeURIComponent(permissao)}`,
        { method: "DELETE", headers: authHeaders() },
      ).then(handle),

    /**
     * Tira a pessoa do Laboratório: apaga as permissões e o cadastro local.
     * NÃO mexe no Portal — a conta continua existindo lá.
     */
    excluirUsuario: (
      usuario: string,
    ): Promise<{
      ok: boolean;
      usuario: string;
      permissoes_apagadas: number;
      cadastro_local_apagado: boolean;
    }> =>
      fetch(`${BASE}/permissoes/usuario?usuario=${encodeURIComponent(usuario)}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /**
   * LIBERAÇÃO DOS RESULTADOS ao cliente — por OS.
   * Independente do diagnóstico estar pronto: é outra decisão, de outra pessoa.
   */
  liberacao: {
    situacao: (
      os: string,
    ): Promise<{
      os: string;
      liberacao: { liberado_por: string | null; liberado_em: string; observacao: string | null } | null;
      /** Amostras com o diagnóstico ainda não finalizado (1º portão). */
      pendencias: { id: number; amostra: string }[];
      /** Amostras sem aprovação válida do laudo (2º portão, antes de liberar). */
      aprovacoes: PendenciaAprovacao[];
      /**
       * Pendências ADMINISTRATIVAS da OS (3º portão): pagamento em aberto,
       * documento faltando. Vêm todas — as resolvidas também, porque "quem
       * soltou o pagamento e quando" faz parte da resposta.
       */
      retencoes: PendenciaOs[];
      /** Amostras desta OS que JÁ foram entregues ao Relatórios. */
      entregues: { id: number; amostra: string; despachado_em: string }[];
      /**
       * Amostras que a liberação vigente NÃO autorizou — chegaram depois dela,
       * ou tiveram o diagnóstico reaberto. O despacho delas é recusado até a OS
       * ser liberada de novo. Vem vazia enquanto a OS não estiver liberada.
       */
      naoAutorizadas: { id: number; amostra: string }[];
    }> => fetch(`${BASE}/os/${encodeURIComponent(os)}/liberacao`, { headers: authHeaders() }).then(handle),

    /** Manda para o segundo olhar todas as amostras da OS que já têm parecer. */
    solicitarAprovacaoOs: (
      os: string,
    ): Promise<{
      ok: boolean;
      enviados: { id: number; amostra: string }[];
      pendentes: { id: number; amostra: string; motivo: string }[];
    }> =>
      fetch(`${BASE}/os/${encodeURIComponent(os)}/solicitar-aprovacao`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
      }).then(handle),

    liberar: (
      os: string,
      observacao?: string,
    ): Promise<{
      ok: boolean;
      liberado_em: string;
      amostras: number;
      /** O que foi ENTREGUE ao Relatórios na hora da liberação, e o que ficou. */
      entrega: {
        enviados: { id: number; amostra: string }[];
        pendentes: { id: number; amostra: string; motivo: string }[];
      };
    }> =>
      fetch(`${BASE}/os/${encodeURIComponent(os)}/liberar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ observacao: observacao ?? "" }),
      }).then(handle),

    /**
     * Revogar IMPEDE NOVOS ENVIOS — não recolhe o que já saiu. `ja_entregues`
     * diz quantos laudos desta OS o cliente já recebeu (a tela precisa avisar).
     */
    revogar: (
      os: string,
    ): Promise<{ ok: boolean; os: string; ja_entregues: number; aviso: string | null }> =>
      fetch(`${BASE}/os/${encodeURIComponent(os)}/liberar`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /**
   * PENDÊNCIAS DA OS — o que retém a entrega mesmo com o laboratório pronto
   * (pagamento em aberto, documento faltando). Ver server/pendencias-os.ts.
   */
  pendencias: {
    listar: (os: string): Promise<{ os: string; pendencias: PendenciaOs[]; tipos: string[] }> =>
      fetch(`${BASE}/os/${encodeURIComponent(os)}/pendencias`, { headers: authHeaders() }).then(handle),

    criar: (os: string, tipo: string, descricao: string): Promise<{ ok: boolean; pendencia: PendenciaOs }> =>
      fetch(`${BASE}/os/${encodeURIComponent(os)}/pendencias`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ tipo, descricao }),
      }).then(handle),

    /** Dar baixa: restrito a quem pode liberar (é autorizar a entrega). */
    resolver: (id: number, resolucao?: string): Promise<{ ok: boolean; pendencia: PendenciaOs }> =>
      fetch(`${BASE}/pendencias/${id}/resolver`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ resolucao: resolucao ?? "" }),
      }).then(handle),

    reabrir: (id: number): Promise<{ ok: boolean; pendencia: PendenciaOs }> =>
      fetch(`${BASE}/pendencias/${id}/reabrir`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
      }).then(handle),

    remover: (id: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/pendencias/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle),
  },

  /** IA — melhora o texto do parecer técnico (ChatGPT se houver chave; senão motor local). */
  gpt: {
    melhorarParecer: (text: string): Promise<{ improved: string; mode: "online" | "local" }> =>
      fetch(`${BASE}/gpt/melhorar-parecer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ text }),
      }).then(handle),
  },

  /** Aba HISTORICO — análises despachadas pelo próprio Laboratório. */
  historico: {
    listar: (): Promise<{ historico: HistoricoItem[] }> =>
      fetch(`${BASE}/historico`, { headers: authHeaders() }).then(handle),

    /** O laudo INTEIRO na tela (dois cliques na linha) — sem baixar o .docx. */
    obter: (id: number): Promise<{ analise: LaudoCompleto; aprovacao: AprovacaoLaudo | null }> =>
      fetch(`${BASE}/historico/${id}`, { headers: authHeaders() }).then(handle),

    /** Baixa o laudo (.docx) da análise e dispara o download no navegador. */
    baixarDocumento: async (id: number, fileName: string): Promise<void> => {
      const res = await fetch(`${BASE}/historico/${id}/documento`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        let msg = `Erro ${res.status}`;
        let code = "ERROR";
        try {
          const body = await res.json();
          if (body?.error) msg = body.error;
          if (body?.code) code = body.code;
        } catch {
          /* corpo não-JSON */
        }
        throw new ApiError(msg, code);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    },
  },
  /** Aprovar / reprovar (a aba "Aprovação de relatório"). */
  aprovacoes: {
    aprovar: (id: number): Promise<any> =>
      fetch(`${BASE}/aprovacoes/${id}/aprovar`, { method: "POST", headers: authHeaders() }).then(handle),

    reprovar: (id: number, motivo: string): Promise<any> =>
      fetch(`${BASE}/aprovacoes/${id}/reprovar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ motivo }),
      }).then(handle),
  },

  /**
   * MATERIAL DO CLIENTE — conferência por leitor de código de barras.
   * `bipar` usa um handle próprio porque o 409/duplicado precisa chegar na tela
   * como informação, não como exceção (ver ResultadoBipagem).
   */
  /**
   * Limites normativos: a IA transcreve a norma para o cadastro.
   *
   * Nada do que vem daqui vale no diagnóstico até alguém CONFERIR — as faixas
   * nascem desligadas de propósito (ver server/limites-ia.ts).
   */
  limites: {
    sugerir: async (dados: {
      texto: string;
      norma: string;
      contexto?: string;
    }): Promise<{
      ok: boolean;
      criadas: number;
      descartadas: number;
      mensagem: string;
      linhas?: Row[];
      detalhes?: { motivo: string; linha: Row }[];
    }> => {
      const res = await fetch(`${BASE}/limites/sugerir`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      });
      const body = await res.json().catch(() => null);
      // "nenhuma faixa aproveitável" volta como 200 com ok:false — é resposta,
      // não erro: a tela mostra o que a IA não conseguiu ler.
      if (!res.ok) throw new ApiError(body?.error ?? `Erro ${res.status}`, body?.code ?? "ERROR");
      return body;
    },

    conferir: (id: number): Promise<{ ok: boolean; mensagem: string }> =>
      fetch(`${BASE}/limites/${id}/conferir`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: "{}",
      }).then(handle),
  },

  material: {
    conferencia: (id: number): Promise<ConferenciaMaterial> =>
      fetch(`${BASE}/material/${id}/itens`, { headers: authHeaders() }).then(handle),

    bipar: async (
      id: number,
      codigo: string,
      extra?: { descricao?: string; tipo?: string; origem?: "leitor" | "camera" },
    ): Promise<ResultadoBipagem> => {
      const res = await fetch(`${BASE}/material/${id}/bipar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ codigo, ...extra }),
      });
      const body = await res.json().catch(() => null);
      // 409 aqui é "remessa já enviada" — esse sim é erro de verdade.
      if (!res.ok && !body?.duplicado) {
        throw new ApiError(body?.error ?? `Erro ${res.status}`, body?.code ?? "ERROR");
      }
      return body as ResultadoBipagem;
    },

    removerItem: (id: number, itemId: number): Promise<{ ok: boolean; conferido: number }> =>
      fetch(`${BASE}/material/${id}/itens/${itemId}`, { method: "DELETE", headers: authHeaders() }).then(handle),

    /** Fecha a conferência. Com divergência, o servidor devolve 409 pedindo confirmação. */
    fechar: async (id: number, forcar = false): Promise<any> => {
      const res = await fetch(`${BASE}/material/${id}/fechar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ forcar }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok && !body?.divergencia) {
        throw new ApiError(body?.error ?? `Erro ${res.status}`, body?.code ?? "ERROR");
      }
      return body;
    },

    enviar: (id: number, rastreio?: string): Promise<any> =>
      fetch(`${BASE}/material/${id}/enviar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ rastreio }),
      }).then(handle),

    /**
     * Os 3 impressos da remessa (HTML pronto para Ctrl+P → salvar em PDF).
     *
     * Vem por FETCH, e não por `<a href>`: o navegador não manda cabeçalho
     * customizado numa navegação, então o link direto chegava ao servidor sem o
     * `x-sercamp-usuario` e a aba nova abria com o JSON de "faça login" no lugar
     * do documento. Quem abre a janela é a tela, com o HTML já em mãos.
     */
    documento: (id: number, doc: "declaracao" | "etiqueta" | "conferencia"): Promise<string> =>
      fetch(`${BASE}/material/${id}/${doc}`, { headers: authHeaders() }).then(async (r) => {
        if (!r.ok) {
          let msg = `Erro ${r.status}`;
          try {
            const b = await r.json();
            if (b?.error) msg = b.error;
          } catch {
            /* o erro nem sempre é JSON */
          }
          throw new ApiError(msg);
        }
        return r.text();
      }),
  },

  /**
   * O que está por trás de um número do painel (dois cliques no card).
   * Ver server/dashboard-cards.ts.
   */
  dashboardCard: {
    detalhar: (card: string): Promise<DetalheCard> =>
      fetch(`${BASE}/dashboard/cards/${encodeURIComponent(card)}`, { headers: authHeaders() }).then(handle),
  },

  /**
   * A LUPA DO CAMPO "OS": procura a ordem de serviço no Relatórios, aqui no
   * Laboratório e na Gestão da Operação de uma vez só — ver server/os-busca.ts.
   */
  os: {
    buscar: (q: string, signal?: AbortSignal): Promise<{ itens: OsEncontrada[]; avisos: string[] }> =>
      fetch(`${BASE}/os/buscar?q=${encodeURIComponent(q)}`, { headers: authHeaders(), signal }).then(handle),
  },

  /**
   * CONTROLE DE SERINGAS (ver server/seringas.ts).
   *
   * As três bipagens — cadastrar, mandar para fora e receber de volta — seguem
   * a mesma regra da conferência de material: DUPLICADA NÃO É ERRO. O servidor
   * responde 200 com `duplicada: true` e a tela mostra em amarelo, sem parar o
   * trabalho de quem está com a caixa aberta na frente.
   */
  seringas: {
    resumo: (): Promise<ResumoSeringas> =>
      fetch(`${BASE}/seringas/resumo`, { headers: authHeaders() }).then(handle),

    estoque: (
      filtros: { q?: string; situacao?: string } = {},
    ): Promise<{ seringas: Row[]; total: number }> => {
      const p = new URLSearchParams();
      if (filtros.q) p.set("q", filtros.q);
      if (filtros.situacao) p.set("situacao", filtros.situacao);
      return fetch(`${BASE}/seringas/estoque?${p}`, { headers: authHeaders() }).then(handle);
    },

    cadastrar: async (
      codigo: string,
      extra?: { tipo?: string; lote?: string; observacao?: string; origem?: "leitor" | "camera" },
    ): Promise<ResultadoCadastroSeringa> => {
      const res = await fetch(`${BASE}/seringas/cadastrar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ codigo, ...extra }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok && !body?.duplicada) {
        throw new ApiError(body?.error ?? `Erro ${res.status}`, body?.code ?? "ERROR");
      }
      return body as ResultadoCadastroSeringa;
    },

    /** A prévia da numeração automática ("vão entrar da SER-0051 à SER-0100"). */
    proximaFaixa: (
      prefixo: string,
      quantidade: number,
      signal?: AbortSignal,
    ): Promise<FaixaSeringas> =>
      fetch(
        `${BASE}/seringas/proxima-faixa?prefixo=${encodeURIComponent(prefixo)}&quantidade=${quantidade}`,
        { headers: authHeaders(), signal },
      ).then(handle),

    /** Cadastra a caixa inteira de uma vez, com a numeração da casa. */
    cadastrarLote: (dados: {
      quantidade: number;
      tipo?: string;
      lote?: string;
      prefixo?: string;
      observacao?: string;
    }): Promise<ResultadoLoteSeringas> =>
      fetch(`${BASE}/seringas/cadastrar-lote`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    atualizar: (id: number, dados: Record<string, unknown>): Promise<{ ok: boolean; seringa: Row }> =>
      fetch(`${BASE}/seringas/estoque/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    remover: (id: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/seringas/estoque/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle),

    /** Tira da entrada em andamento a seringa que acabou de ser bipada. */
    desfazerCadastro: (codigo: string): Promise<{ ok: boolean; codigo: string; total: number }> =>
      fetch(`${BASE}/seringas/desfazer-cadastro`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ codigo }),
      }).then(handle),

    /** Desfaz a devolução bipada por engano: a seringa volta a constar como fora. */
    desfazerRetorno: (id: number, itemId: number): Promise<{ ok: boolean; mensagem?: string }> =>
      fetch(`${BASE}/seringas/saidas/${id}/itens/${itemId}/desfazer-retorno`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    clientes: (q: string, signal?: AbortSignal): Promise<{ clientes: ClienteSeringa[] }> =>
      fetch(`${BASE}/seringas/clientes?q=${encodeURIComponent(q)}`, {
        headers: authHeaders(),
        signal,
      }).then(handle),

    saidas: (
      filtros: { q?: string; status?: string } = {},
    ): Promise<{ saidas: Row[]; total: number }> => {
      const p = new URLSearchParams();
      if (filtros.q) p.set("q", filtros.q);
      if (filtros.status) p.set("status", filtros.status);
      return fetch(`${BASE}/seringas/saidas?${p}`, { headers: authHeaders() }).then(handle);
    },

    saida: (id: number): Promise<DetalheSaidaSeringas> =>
      fetch(`${BASE}/seringas/saidas/${id}`, { headers: authHeaders() }).then(handle),

    /** Laboratórios externos já usados — sugestão no formulário de saída. */
    laboratoriosExternos: (): Promise<{ laboratorios: string[] }> =>
      fetch(`${BASE}/seringas/laboratorios-externos`, { headers: authHeaders() }).then(handle),

    criarSaida: (dados: Record<string, unknown>): Promise<{ ok: boolean; saida: Row }> =>
      fetch(`${BASE}/seringas/saidas`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    atualizarSaida: (id: number, dados: Record<string, unknown>): Promise<{ ok: boolean; saida: Row }> =>
      fetch(`${BASE}/seringas/saidas/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    removerSaida: (id: number): Promise<{ ok: boolean; mensagem?: string }> =>
      fetch(`${BASE}/seringas/saidas/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle),

    /**
     * Bipa a seringa NA SAÍDA. Além de `duplicada`, esta responde
     * `desconhecida` (código que não está cadastrado) — a tela transforma isso
     * no atalho "cadastrar agora", em vez de mandar a pessoa sair da caixa.
     */
    biparSaida: async (
      id: number,
      codigo: string,
      origem: "leitor" | "camera" = "leitor",
    ): Promise<ResultadoBipagemSeringa> => {
      const res = await fetch(`${BASE}/seringas/saidas/${id}/bipar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ codigo, origem }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok && !body?.duplicada && !body?.desconhecida) {
        throw new ApiError(body?.error ?? `Erro ${res.status}`, body?.code ?? "ERROR");
      }
      return body as ResultadoBipagemSeringa;
    },

    removerItemSaida: (id: number, itemId: number): Promise<{ ok: boolean; bipadas: number }> =>
      fetch(`${BASE}/seringas/saidas/${id}/itens/${itemId}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),

    /** Confirma a saída. Com divergência, o servidor devolve 409 pedindo confirmação. */
    confirmarSaida: async (id: number, forcar = false): Promise<any> => {
      const res = await fetch(`${BASE}/seringas/saidas/${id}/confirmar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ forcar }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok && !body?.divergencia) {
        throw new ApiError(body?.error ?? `Erro ${res.status}`, body?.code ?? "ERROR");
      }
      return body;
    },

    retornar: async (
      id: number,
      codigo: string,
      origem: "leitor" | "camera" = "leitor",
    ): Promise<ResultadoBipagemSeringa> => {
      const res = await fetch(`${BASE}/seringas/saidas/${id}/retornar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ codigo, origem }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok && !body?.duplicada) {
        throw new ApiError(body?.error ?? `Erro ${res.status}`, body?.code ?? "ERROR");
      }
      return body as ResultadoBipagemSeringa;
    },

    /** Encerra a saída. Faltando seringa, o servidor devolve 409 avisando das faltantes. */
    concluirSaida: async (id: number, forcar = false, motivo?: string): Promise<any> => {
      const res = await fetch(`${BASE}/seringas/saidas/${id}/concluir`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ forcar, motivo }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok && !body?.pendencia) {
        throw new ApiError(body?.error ?? `Erro ${res.status}`, body?.code ?? "ERROR");
      }
      return body;
    },
  },

  /* ==================================================================
   *  EQUIPAMENTOS DE ENSAIO — bloco IDÊNTICO no Laboratório e na Gestão da
   *  Operação (a tela components/EquipamentosEnsaioView.tsx é o mesmo
   *  arquivo nos dois). Copie junto os tipos e o `pedirEq`, lá no fim deste
   *  arquivo. O dono dos dados é a Gestão da Operação; no Laboratório o
   *  servidor só repassa (server/equipamentos-ensaio.ts).
   *
   *  Mesmas regras de bancada das seringas: `duplicada`, `desconhecida` e
   *  `semCodigo` da bipagem, `divergencia` da confirmação e `pendencia` do
   *  encerramento NÃO viram exceção — a tela mostra em amarelo. E o conflito
   *  de nº de série / código no cadastro volta como resultado (com o
   *  equipamento que já existe), porque a tela oferece o que fazer com ele.
   * ================================================================== */
  equipamentosEnsaio: {
    resumo: (): Promise<ResumoEquipEnsaio> => pedirEq("resumo"),

    estoque: (
      filtros: { q?: string; situacao?: string; calibracao?: string; semCodigo?: boolean } = {},
    ): Promise<{ equipamentos: EquipEnsaio[]; total: number }> => {
      const p = new URLSearchParams();
      if (filtros.q) p.set("q", filtros.q);
      if (filtros.situacao) p.set("situacao", filtros.situacao);
      if (filtros.calibracao) p.set("calibracao", filtros.calibracao);
      if (filtros.semCodigo) p.set("semCodigo", "1");
      return pedirEq(`estoque?${p}`);
    },

    /** Um equipamento com todos os certificados dele. */
    equipamento: (id: number): Promise<{ equipamento: EquipEnsaio; certificados: CertificadoEquip[] }> =>
      pedirEq(`estoque/${id}`),

    /** Só a PRÉVIA do próximo código livre — o de verdade sai na hora de gravar. */
    proximoCodigo: (prefixo = "EQ-", signal?: AbortSignal): Promise<{ prefixo: string; codigo: string }> =>
      pedirEq(`proximo-codigo?prefixo=${encodeURIComponent(prefixo)}`, { signal }),

    /** Não lança em SERIE_DUPLICADA / CODIGO_DUPLICADO: devolve o equipamento que já existe. */
    cadastrar: async (dados: {
      nome: string;
      fabricante: string;
      modelo: string;
      numero_serie: string;
      codigo_barras?: string;
      gerar_codigo?: boolean;
      prefixo?: string;
      observacao?: string;
      origem?: "leitor" | "camera";
    }): Promise<ResultadoSalvarEquip> =>
      resultadoSalvar(await pedirEq("cadastrar", { metodo: "POST", json: dados, tolera: ehConflitoCadastro })),

    /** Só as chaves PRESENTES mudam (ver o contrato). Conflitos voltam como resultado. */
    atualizar: async (id: number, dados: Record<string, unknown>): Promise<ResultadoSalvarEquip> =>
      resultadoSalvar(await pedirEq(`estoque/${id}`, { metodo: "PUT", json: dados, tolera: ehConflitoCadastro })),

    darBaixa: (id: number, motivo: string): Promise<{ ok: boolean; equipamento?: EquipEnsaio }> =>
      pedirEq(`estoque/${id}/baixa`, { metodo: "POST", json: { motivo } }),

    voltarDaBaixa: (id: number): Promise<{ ok: boolean; equipamento?: EquipEnsaio }> =>
      pedirEq(`estoque/${id}/voltar-da-baixa`, { metodo: "POST", json: {} }),

    /** Dá código novo (prefixo EQ-) a quem AINDA não tem — até 100 de uma vez. */
    gerarCodigos: (ids: number[]): Promise<ResultadoGerarCodigos> =>
      pedirEq("gerar-codigos", { metodo: "POST", json: { ids } }),

    /** Grava o código que o leitor acabou de ler num equipamento que não tinha código. */
    codigoDoLeitor: (id: number, codigo: string): Promise<{ ok: boolean; equipamento?: EquipEnsaio }> =>
      pedirEq(`estoque/${id}/codigo-do-leitor`, { metodo: "POST", json: { codigo } }),

    remover: (id: number): Promise<{ ok: boolean }> => pedirEq(`estoque/${id}`, { metodo: "DELETE" }),

    /** O "desfazer" de quem acabou de cadastrar (só o próprio autor, em 24 h, sem uso). */
    desfazerCadastro: (id: number, codigo_barras: string): Promise<{ ok: boolean }> =>
      pedirEq("desfazer-cadastro", { metodo: "POST", json: { id, codigo_barras } }),

    /** Só LÊ o PDF (datas, nº, série…) — nada é gravado. */
    lerCertificado: (file: File, meses: number): Promise<LeituraCertificadoEquip> => {
      const fd = new FormData();
      fd.append("file", file);
      return pedirEq(`certificado/ler?meses=${meses}`, { metodo: "POST", form: fd });
    },

    /** Anexa o certificado (PDF opcional: só as datas também vale — certificado em papel). */
    salvarCertificado: (
      id: number,
      dados: {
        file?: File | null;
        data_calibracao: string;
        data_validade: string;
        numero?: string;
        lido_do_pdf?: boolean;
      },
    ): Promise<{ ok: boolean; certificado_id: number | null; mensagem?: string }> => {
      const fd = new FormData();
      if (dados.file) fd.append("file", dados.file);
      fd.append("data_calibracao", dados.data_calibracao);
      fd.append("data_validade", dados.data_validade);
      fd.append("numero", dados.numero ?? "");
      fd.append("lido_do_pdf", String(!!dados.lido_do_pdf));
      return pedirEq(`estoque/${id}/certificado`, { metodo: "POST", form: fd });
    },

    certificados: (id: number): Promise<{ certificados: CertificadoEquip[] }> =>
      pedirEq(`estoque/${id}/certificados`),

    /**
     * Abre o PDF numa aba nova. NUNCA por `<a href>`: o navegador não manda o
     * cabeçalho do usuário num link, e o servidor responde 401. A janela abre
     * ANTES da espera (aberta depois, o navegador a trata como pop-up não
     * pedido e bloqueia) e recebe o arquivo já baixado, como blob.
     */
    abrirCertificado: async (certId: number): Promise<void> => {
      const janela = window.open("", "_blank");
      if (!janela) {
        throw new ApiError(
          "O navegador bloqueou a janela. Libere os pop-ups para este site e tente de novo.",
          "POPUP",
        );
      }
      try {
        janela.document.title = "Abrindo o certificado…";
        janela.document.body.textContent = "Abrindo o certificado…";
      } catch {
        /* janela de outra origem: só não mostra o "abrindo" */
      }
      try {
        const res = await fetch(`${BASE}/equipamentos-ensaio/certificados/${certId}/arquivo`, {
          headers: authHeaders(),
        });
        if (!res.ok) {
          const corpo = await res.json().catch(() => null);
          throw new ApiError(corpo?.error ?? `Erro ${res.status} ao abrir o certificado.`, corpo?.code ?? "ERROR");
        }
        const bytes = await res.arrayBuffer();
        const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
        janela.location.href = url;
        // Revogar na hora mataria o PDF antes de a aba terminar de carregar.
        setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
      } catch (err) {
        janela.close();
        if (err instanceof ApiError) throw err;
        throw new ApiError("Não foi possível baixar o certificado — tente de novo.", "REDE");
      }
    },

    removerCertificado: (certId: number): Promise<{ ok: boolean }> =>
      pedirEq(`certificados/${certId}`, { metodo: "DELETE" }),

    clientes: (q: string, signal?: AbortSignal): Promise<{ clientes: ClienteEquip[] }> =>
      pedirEq(`clientes?q=${encodeURIComponent(q)}`, { signal }),

    laboratoriosExternos: (): Promise<{ laboratorios: string[] }> => pedirEq("laboratorios-externos"),

    saidas: (filtros: { q?: string; status?: string } = {}): Promise<{ saidas: SaidaEquip[]; total: number }> => {
      const p = new URLSearchParams();
      if (filtros.q) p.set("q", filtros.q);
      if (filtros.status) p.set("status", filtros.status);
      return pedirEq(`saidas?${p}`);
    },

    saida: (id: number): Promise<DetalheSaidaEquip> => pedirEq(`saidas/${id}`),

    criarSaida: (dados: Record<string, unknown>): Promise<{ ok: boolean; saida: SaidaEquip }> =>
      pedirEq("saidas", { metodo: "POST", json: dados }),

    atualizarSaida: (id: number, dados: Record<string, unknown>): Promise<{ ok: boolean; saida: SaidaEquip }> =>
      pedirEq(`saidas/${id}`, { metodo: "PUT", json: dados }),

    removerSaida: (id: number): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}`, { metodo: "DELETE" }),

    /** Não lança em `duplicada`, `desconhecida` nem `semCodigo` (ver o topo do bloco). */
    biparSaida: (id: number, codigo: string, origem: "leitor" | "camera" = "leitor"): Promise<ResultadoBipagemEquip> =>
      pedirEq(`saidas/${id}/bipar`, {
        metodo: "POST",
        json: { codigo, origem },
        tolera: (c) => !!(c?.duplicada || c?.desconhecida || c?.semCodigo),
      }),

    removerItemSaida: (id: number, itemId: number): Promise<{ ok: boolean }> =>
      pedirEq(`saidas/${id}/itens/${itemId}`, { metodo: "DELETE" }),

    /** Com divergência, o servidor pede confirmação (409 com `divergencia`, que não lança). */
    confirmarSaida: (
      id: number,
      forcar = false,
    ): Promise<{ ok: boolean; divergencia?: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/confirmar`, {
        metodo: "POST",
        json: { forcar },
        tolera: (c) => !!c?.divergencia,
      }),

    retornar: (id: number, codigo: string, origem: "leitor" | "camera" = "leitor"): Promise<ResultadoBipagemEquip> =>
      pedirEq(`saidas/${id}/retornar`, {
        metodo: "POST",
        json: { codigo, origem },
        tolera: (c) => !!c?.duplicada,
      }),

    desfazerRetorno: (id: number, itemId: number): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/itens/${itemId}/desfazer-retorno`, { metodo: "POST", json: {} }),

    /** Faltando equipamento, o servidor pede confirmação (409 com `pendencia`, que não lança). */
    concluirSaida: (
      id: number,
      forcar = false,
      motivo?: string,
    ): Promise<{ ok: boolean; pendencia?: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/concluir`, {
        metodo: "POST",
        json: { forcar, motivo },
        tolera: (c) => !!c?.pendencia,
      }),

    /*
     * CORREÇÃO DE SAÍDA JÁ CONFIRMADA (quem pode: permissão "Corrigir saída de
     * equipamento" no Laboratório; administrador na Gestão). Toda correção
     * pede o motivo e fica registrada na saída.
     */
    corrigirDados: (
      id: number,
      dados: Record<string, unknown>,
      motivo: string,
    ): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/corrigir`, { metodo: "PUT", json: { ...dados, motivo } }),

    /** Troca um equipamento da lista pelo que de fato foi (`aindaFora`: está com a equipe). */
    corrigirTrocar: (
      id: number,
      itemId: number,
      codigo: string,
      aindaFora: boolean,
      motivo: string,
    ): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/corrigir/trocar`, {
        metodo: "POST",
        json: { item_id: itemId, codigo, ainda_fora: aindaFora, motivo },
      }),

    corrigirTirar: (id: number, itemId: number, motivo: string): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/corrigir/tirar`, { metodo: "POST", json: { item_id: itemId, motivo } }),

    corrigirIncluir: (
      id: number,
      codigo: string,
      aindaFora: boolean,
      motivo: string,
    ): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/corrigir/incluir`, {
        metodo: "POST",
        json: { codigo, ainda_fora: aindaFora, motivo },
      }),
  },
};

/** Os números do Controle de Seringas (alimentam o submenu e o cabeçalho). */
export interface ResumoSeringas {
  estoque: {
    emEstoque: number;
    emServico: number;
    comCliente: number;
    /** Enviadas a um laboratório de fora (análise terceirizada). */
    emLabExterno?: number;
    baixadas: number;
    /** Saíram e não voltaram (devolução encerrada com menos). */
    faltantes: number;
    total: number;
  };
  saidas: { emSeparacao: number; enviadas: number; concluidas: number };
  /** Quantas seringas estão fora agora esperando retorno. */
  aguardandoRetorno: number;
}

/** Um cliente encontrado pela busca por nome ou CNPJ (ver server/seringas.ts). */
export interface ClienteSeringa {
  nome: string;
  cnpj: string | null;
  /** Onde ele foi achado: cadastro, material, análise, equipamento. */
  fontes: string[];
}

/** A numeração que a entrada por quantidade vai usar (prévia, sem gravar). */
export interface FaixaSeringas {
  prefixo: string;
  quantidade: number;
  primeiro: string | null;
  ultimo: string | null;
  codigos: string[];
}

/** O que voltou de uma entrada por quantidade — é daqui que saem as etiquetas. */
export interface ResultadoLoteSeringas {
  ok: boolean;
  criadas: { id: number; codigo: string }[];
  quantidade: number;
  primeiro: string | null;
  ultimo: string | null;
  total: number;
  mensagem: string;
}

export interface ResultadoCadastroSeringa {
  ok: boolean;
  duplicada?: boolean;
  seringa?: Row;
  mensagem?: string;
  /** Quantas estão no estoque depois desta bipagem. */
  total?: number;
}

/** Resposta das bipagens de saída e de retorno. */
export interface ResultadoBipagemSeringa {
  ok: boolean;
  duplicada?: boolean;
  /** Só na saída: o código não está cadastrado. */
  desconhecida?: boolean;
  codigo?: string;
  item?: Row;
  mensagem?: string;
  bipadas?: number;
  retornadas?: number;
  pendentes?: number;
  previsto?: number;
  completo?: boolean;
  /** Só no retorno: a última seringa voltou e a saída se encerrou sozinha. */
  fechada?: boolean;
}

/** Uma saída aberta na tela, com as seringas dela. */
export interface DetalheSaidaSeringas {
  saida: Row;
  itens: Row[];
  bipadas: number;
  retornadas: number;
  pendentes: number;
  previsto: number;
}

/** Os registros por trás de um card do painel, já com as colunas prontas. */
export interface DetalheCard {
  card: string;
  titulo: string;
  descricao: string;
  total: number;
  colunas: { chave: string; label: string }[];
  /** Cada linha traz as chaves das colunas + `tom` (ok/alerta/neutro). */
  itens: Record<string, string | number | null>[];
}

/** Uma OS como os sistemas do portal a conhecem, já consolidada. */
export interface OsEncontrada {
  numero: string;
  cliente?: string | null;
  equipamento?: string | null;
  responsavel?: string | null;
  solicitante?: string | null;
  tipo_servico?: string | null;
  local?: string | null;
  cidade?: string | null;
  uf?: string | null;
  inicio?: string | null;
  fim?: string | null;
  status?: string | null;
  /** Endereço do destinatário reaproveitado de uma remessa anterior. */
  destinatario?: Record<string, string> | null;
  fontes: string[];
  detalhes: string[];
  ja_tem_remessa?: boolean;
}

/* ======================================================================
 *  EQUIPAMENTOS DE ENSAIO — o "pedir" e os tipos do bloco
 *  api.equipamentosEnsaio. IGUAIS no Laboratório e na Gestão da Operação.
 * ==================================================================== */

interface PedidoEq {
  metodo?: "GET" | "POST" | "PUT" | "DELETE";
  json?: unknown;
  /** Multipart (PDF do certificado): o navegador põe o Content-Type com o boundary. */
  form?: FormData;
  signal?: AbortSignal;
  /** Respostas de erro que NÃO viram exceção — a tela trata como aviso. */
  tolera?: (corpo: any) => boolean;
}

/**
 * Uma chamada às rotas /api/equipamentos-ensaio/...
 *
 * Existe (em vez do `handle` de sempre) por causa do estado "indisponível" da
 * tela: os dados moram na Gestão da Operação, e quando ela está fora, ou
 * desatualizada, a resposta pode nem ser JSON. Cada falha vira um ApiError com
 * código — REDE (sem resposta), RESPOSTA_INVALIDA (veio HTML/lixo) ou o `code`
 * do servidor (OPERACIONAL_DOWN, OPERACIONAL_DESATUALIZADA, INTEGRACAO_CONFIG…)
 * — para a tela mostrar o aviso certo em vez de uma lista vazia que mente.
 */
async function pedirEq<T = any>(caminho: string, p: PedidoEq = {}): Promise<T> {
  const headers: Record<string, string> = { ...authHeaders() };
  let body: BodyInit | undefined;
  if (p.form) body = p.form;
  else if (p.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(p.json);
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}/equipamentos-ensaio/${caminho}`, {
      method: p.metodo ?? "GET",
      headers,
      body,
      signal: p.signal,
    });
  } catch (err) {
    // Busca cancelada de propósito (a pessoa continuou digitando): segue como está.
    if ((err as Error)?.name === "AbortError") throw err;
    throw new ApiError("Sem resposta do servidor — confira a conexão e tente de novo.", "REDE");
  }

  const texto = await res.text().catch(() => "");
  let corpo: any = null;
  let jsonValido = true;
  try {
    corpo = texto ? JSON.parse(texto) : null;
  } catch {
    jsonValido = false;
  }

  if (!jsonValido || (res.ok && corpo === null)) {
    throw new ApiError(
      `O servidor respondeu algo inesperado (${res.status}) — os equipamentos de ensaio não puderam ser lidos. Tente de novo em instantes.`,
      "RESPOSTA_INVALIDA",
    );
  }
  if (!res.ok) {
    if (corpo && p.tolera?.(corpo)) return corpo as T;
    throw new ApiError(corpo?.error ?? corpo?.mensagem ?? `Erro ${res.status}`, corpo?.code ?? "ERROR");
  }
  return corpo as T;
}

/** 409 de nº de série / código já usados, COM o equipamento que já tem — a tela decide o que fazer. */
function ehConflitoCadastro(c: any): boolean {
  return (c?.code === "SERIE_DUPLICADA" || c?.code === "CODIGO_DUPLICADO") && !!c?.existente;
}

function resultadoSalvar(c: any): ResultadoSalvarEquip {
  if (c && ehConflitoCadastro(c)) {
    return { ok: false, code: c.code, error: String(c.error ?? ""), existente: c.existente };
  }
  return { ok: true, equipamento: c.equipamento, mensagem: c.mensagem, aviso: c.aviso };
}

/** Os números da aba (painel, cartões, quadro do estoque). */
export interface ResumoEquipEnsaio {
  estoque: {
    emEstoque: number;
    emServico: number;
    comCliente: number;
    emLabExterno: number;
    faltantes: number;
    baixados: number;
    total: number;
    /** Cadastrados sem código de barras (os antigos, antes desta aba). */
    semCodigo: number;
  };
  calibracao: { vencidas: number; aVencer: number; semValidade: number };
  saidas: { emSeparacao: number; enviadas: number; concluidas: number };
  /** Quantos equipamentos estão fora AGORA esperando retorno. */
  aguardandoRetorno: number;
}

/** Um equipamento de ensaio, como as listas devolvem (cadastro + onde está). */
export interface EquipEnsaio {
  id: number;
  nome: string;
  /** A "Marca" da tela (a coluna se chama fabricante desde sempre). */
  fabricante: string | null;
  modelo: string | null;
  numero_serie: string | null;
  codigo_barras: string | null;
  tipo: string | null;
  observacao: string | null;
  /** A "Condição de uso" (Disponível, Em atividade, Em manutenção...). */
  autorizacao: string | null;
  data_calibracao: string | null;
  data_validade: string | null;
  /** Calculada: Bloqueado / Sem validade / Vencida / A vencer / Válida. */
  situacao_calibracao: string | null;
  /** Onde está: Em estoque, Em serviço, Com cliente, Em laboratório externo, Faltante, Baixado. */
  estoque_situacao: string;
  estoque_saida_id: number | null;
  estoque_atualizado_em: string | null;
  cadastrado_por: string | null;
  created_at: string | null;
  certificado_id: number | null;
  certificado_numero: string | null;
  destino: string | null;
  os_numero: string | null;
  cliente_nome: string | null;
  cliente_cnpj: string | null;
  laboratorio_externo: string | null;
  saida_data: string | null;
  saida_status: string | null;
  /** "Em uso · OS 1234" — OS aberta no Relatórios. */
  em_uso: string | null;
  /** OS de uma liberação em aberto (aba Liberações da Gestão da Operação). */
  liberacao_os: string | null;
}

/** O equipamento que já tem o nº de série (ou o código) que se tentou gravar. */
export interface EquipExistente {
  id: number;
  nome: string;
  fabricante: string | null;
  modelo: string | null;
  numero_serie: string | null;
  codigo_barras: string | null;
  estoque_situacao: string | null;
  em_uso: string | null;
}

export type ResultadoSalvarEquip =
  | { ok: true; equipamento: EquipEnsaio; mensagem?: string; aviso?: string }
  | {
      ok: false;
      code: "SERIE_DUPLICADA" | "CODIGO_DUPLICADO";
      error: string;
      existente: EquipExistente;
    };

export interface ResultadoGerarCodigos {
  ok: boolean;
  gerados: { id: number; nome: string; codigo_barras: string }[];
  ignorados: { id: number; motivo: string }[];
}

/** Um certificado anexado (sem o arquivo). */
export interface CertificadoEquip {
  id: number;
  nome_arquivo: string | null;
  numero: string | null;
  data_calibracao: string | null;
  data_validade: string | null;
  lido_do_pdf: boolean | null;
  enviado_por: string | null;
  criado_em: string | null;
  tamanho: number | null;
}

/** O que a leitura do PDF achou (ver sistemaOperacional/server/certificado-calibracao.ts). */
export interface LeituraCertificadoEquip {
  ok: boolean;
  data_calibracao: string | null;
  /** Sugerida = calibração + N meses (o certificado real não traz validade). */
  data_validade: string | null;
  numero_certificado: string | null;
  numero_serie: string | null;
  modelo: string | null;
  fabricante: string | null;
  equipamento: string | null;
  datas_encontradas: string[];
  aviso: string | null;
  nome_arquivo?: string;
  tamanho?: number;
}

/** Um lote de saída (é a lista do Retorno também). */
export interface SaidaEquip {
  id: number;
  /** 'Serviço' | 'Cliente' | 'Laboratório externo' */
  destino: string;
  os_numero: string | null;
  cliente_nome: string | null;
  cliente_cnpj: string | null;
  laboratorio_externo: string | null;
  data: string | null;
  quantidade_prevista: number;
  observacao: string | null;
  responsavel: string | null;
  /** 'Em separação' | 'Enviada' | 'Concluída' */
  status: string;
  origem_sistema: string | null;
  enviada_em: string | null;
  concluida_em: string | null;
  created_at: string | null;
  bipados?: number;
  retornados?: number;
}

/** Um equipamento bipado numa saída. */
export interface ItemSaidaEquip {
  id: number;
  equipamento_id: number | null;
  codigo: string;
  /** Retrato do cadastro na hora da bipagem. */
  equipamento_nome: string | null;
  numero_serie: string | null;
  fabricante: string | null;
  modelo: string | null;
  estoque_situacao: string | null;
  bipado_por: string | null;
  bipado_em: string | null;
  origem: string | null;
  retornado_em: string | null;
  retornado_por: string | null;
  retorno_origem: string | null;
  /** Calculados agora (calibração vencida, em manutenção...) — nada disso bloqueia. */
  alertas: string[];
}

export interface DetalheSaidaEquip {
  saida: SaidaEquip;
  itens: ItemSaidaEquip[];
  bipados: number;
  retornados: number;
  pendentes: number;
  previsto: number;
  /** Correções feitas depois de confirmada (a mais nova primeiro). */
  correcoes?: CorrecaoSaidaEquip[];
  /** Quem está vendo pode corrigir esta saída já confirmada (o servidor decide). */
  pode_corrigir?: boolean;
}

/** Uma correção numa saída já confirmada — fica registrada com o motivo. */
export interface CorrecaoSaidaEquip {
  id: number;
  /** "trocar" | "tirar" | "incluir" | "editar" */
  acao: string;
  detalhe: string;
  motivo: string;
  usuario: string | null;
  /** "laboratorio" | "operacional" — de qual sistema veio. */
  origem: string | null;
  criado_em: string;
}

/** Resposta das bipagens de saída e de retorno. */
export interface ResultadoBipagemEquip {
  ok: boolean;
  duplicada?: boolean;
  /** Só na saída: o código não é de nenhum equipamento. */
  desconhecida?: boolean;
  /** Só na saída: o código lido é o Nº DE SÉRIE de um equipamento ainda sem código. */
  semCodigo?: boolean;
  equipamento?: { id: number; nome: string; modelo: string | null; numero_serie: string | null };
  codigo?: string;
  item?: ItemSaidaEquip;
  mensagem?: string;
  bipados?: number;
  retornados?: number;
  pendentes?: number;
  previsto?: number;
  completo?: boolean;
  /** Só no retorno: o último voltou e a saída se encerrou sozinha. */
  fechada?: boolean;
  /** Só na saída: o que conferir (entrou mesmo assim). */
  alertas?: string[];
  /** Só no retorno: a condição de uso que continua (ex.: "Em manutenção"). */
  aviso?: string;
}

export interface ClienteEquip {
  nome: string;
  cnpj: string | null;
  fontes: string[];
}
